import { Checkbox, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { except } from "@entrilia/shared/utilities/arrayHelper";
import type { MemberRole } from "../../../../../api/types/userManagementTypes";
import { entriliaProductCaptionMap } from "../../../../../utilities/enumCaptions";
import EntriliaProductIcon from "../../../../common/domain-icons/EntriliaProductIcon";
import type { CompanyProductWithRoles } from "../definitions";
import { roleNamesMap } from "../definitions";
import InviteMembersContext from "./InviteMembersContext";
import PermissionSelect from "./PermissionSelect";

interface Props {
  clientCode: string;
  selectedRoles: MemberRole[];
  onSelectedRolesChange: (newSelectedRoles: MemberRole[]) => void;
}

const ProductPermissions = ({ clientCode, selectedRoles, onSelectedRolesChange }: Props) => {
  const { getAvailableProductsWithRoles } = useDefinedContext(InviteMembersContext);

  const productsWithRoles = getAvailableProductsWithRoles(clientCode);
  const availableRoles = productsWithRoles.flatMap((product) => product.roles);
  const selectedAvailableRoles = selectedRoles.filter((role) => availableRoles.includes(role));

  const setSelectedRole = (item: CompanyProductWithRoles, selectedRole: MemberRole) => {
    const newSelectedRoles = except(selectedAvailableRoles, item.roles);
    newSelectedRoles.push(selectedRole);
    onSelectedRolesChange(newSelectedRoles);
  };

  const handleItemSelectionChange = (item: CompanyProductWithRoles, selected: boolean) => {
    const newSelectedRoles = except(selectedAvailableRoles, item.roles);

    if (selected) {
      if (item.roles[0]) {
        newSelectedRoles.push(item.roles[0]);
      }
    }

    onSelectedRolesChange(newSelectedRoles);
  };

  return (
    <Stack spacing={2}>
      <List
        disablePadding
        subheader={
          <Typography sx={{ mb: 1.5 }} variant="subtitle2">
            Product Permissions
          </Typography>
        }
      >
        {productsWithRoles.map((item) => {
          const checked = selectedAvailableRoles.some((role) => item.roles.includes(role));
          return (
            <ListItem disablePadding key={item.key}>
              <Checkbox checked={checked} onChange={(_, checked) => handleItemSelectionChange(item, checked)} />
              <EntriliaProductIcon product={item.key} color="secondary" fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary={<Typography>{entriliaProductCaptionMap[item.key]}</Typography>} />
              {checked && (
                <PermissionSelect
                  selectedValue={item.roles.find((role) => selectedAvailableRoles.includes(role))}
                  onChange={(selectedRole) => setSelectedRole(item, selectedRole)}
                  options={item.roles.map((role) => {
                    return {
                      value: role,
                      label: roleNamesMap[role],
                    };
                  })}
                />
              )}
            </ListItem>
          );
        })}
      </List>
    </Stack>
  );
};

export default ProductPermissions;
